import React, {
    FormEvent,
    PropsWithChildren,
    ReactElement,
    useEffect,
    useState,
} from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import SessionStorage, { clearAquisitionData} from '../../utils/SessionStorage';
import {
    isIOS,
    regexValidation,
    wrapTrademarkSuperscript,
} from '../../utils/cob-utils';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import {
    acquisitionApiRequest,
    AcquisitionInitResponse,
} from '../../utils/acquisition-api/AcquisitionApi';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import {
    AcquisitionFlow,
    getAcquisitionFlow,
    getProgramData,
    areStringsEqual,
    getNameAndDOBFromSessionStorage,
    checkIfInValidAcquisitionFlow,
    getMarketingAttributesForAcquisitions,
    isFundingToggled,
} from '../../utils/acquisition-utils';
import { flowDataModule } from '../../utils/flowDataModule';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { useLoader } from '../../providers/Loader/LoaderProvider';

import axios from 'axios';

import './ApplicationCta.css';
import { useSessionStorageStatus } from '../../providers/SessionStorageStatus/SessionStorageStatusProvider';
import useSessionStorageCheck from '../../hooks/useSessionStorageCheck';
import sortPushupErrors from './sortPushupErrors';
import { blackboxUtils } from '../../utils/blackbox-utils';
import InlinePushup from '../../utils/utility-components/InlinePushup';
import { Button } from '../../importCL';
import {hasDefaultComponent} from '../../utils/pageModelUtils';
import AcquisitionErrorToggleEvent from './AcquisitionErrorToggleEvent';
import { addressValidationSemaphore } from '../PqDataEntryForm/util/addressValidationSemaphore';
import useAcquisitionProviderFacade from '../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade';
import { useFeatureFlags } from '../../providers/FeatureFlagProvider/FeatureFlagProvider';
import { useAllFieldSubscription } from '../../providers/ModernAcquisitionProvider/useSubscription';
import useBriteVerify from '../../hooks/useBriteVerify';
import { generateEmailFormatErrorMessage, generateErrorMessage, generateMonthlyIncomeErrorMessage } from '../../utils/error-message';
import { isNewErrorMessagingFlagActive } from '../../utils/error-message-flag';
import useHandleErrorPushdown from './useHandleErrorPushdown';

export interface IApplicationCtaProps extends MappedComponentProperties {
    ctaText?: string;
    ctaDisclaimerText?: string;
    contentFragmentFolder?: string;
    defaultRedirectUrl?: string;
    dataEntryUrl?: string;
    serverErrorCode?: string;
    serverSessionExpiredErrorCode?: string;
    fundingToggleRedirect?: string;
    splitApplication?: boolean;
    firstPage?: boolean;
}

interface IDisclaimerContent {
    disclaimerText?: string;
}

export const ApplicationCtaEditConfig = {
    emptyLabel: 'Application Cta',
    isEmpty: (props: PropsWithChildren<IApplicationCtaProps>) => {
        return !props || !props.ctaText;
    },
};

const ApplicationCta = (
    props: PropsWithChildren<IApplicationCtaProps>,
): JSX.Element => {
    const { status } = useSessionStorageStatus();
    useSessionStorageCheck();
    const [dynamicDisclaimer, setDynamicDisclaimer] = useState<string>();
    const { showPushdown, closePushdown } = usePushdownContext();
    const { setShouldRenderLoader } = useLoader();
    const [showInlinePushups, setShowInlinePushups] = useState(true);
    const {
        initResponse,
        canFullAppContinue,
        isFundingSectionValid,
        isAuthorisedUserSectionValid,
        isApplicationInfoRecieved,
        isMonthlyIncomeValid,
        isCreditProtectionInfoValid,
        checkIsCreditProtectionValid,
        ssnInfo,
        isSsnInfoValid,
        personalInfoForm,
        personalInfoErrors,
        fundingInfo,
        authorizedUserInfo,
        balanceTransferSelected,
        creditProtectionInfo,
        selectedCardId,
        selectedCardValid,
        isTermsAgreed,
        isTermsValid,
        isConsumerReportAgreed,
        isEsignAgreed,
        isEsignShowing,
        isEsignLinkValid,
        esignVersion,
        setIsAuthorisedUserSectionValid,
        isAUSamePersonCheckDisabled,
        authorizedUserValidity,
        isMonthlyOrYearly,
        validationRef,
        homeAddressPrimaryRef,
    } = useAcquisitionContext();

    const { getFeatureFlags } = useFeatureFlags();
    const { 
        getPersonalInformation, 
        getIsAuthorizedUserSelected, 
        validatePersonalInformation,
        getIsSelectedCardValid,
        getIsCreditProtectionValid,
        getModernFields,
        getIsPhoneNumberValid
    } = useAcquisitionProviderFacade();
    // subscription to all fields for UI changes
    const { fields } = useAllFieldSubscription();
    const { emailValidationApi, phoneValidationApi } = useBriteVerify();
    const { allow, city, zip_code } = regexValidation();
    const isAuthor = ['4502', '3000'].some((entry) => window.location.port.includes(entry));
    const isAdobeMode = SessionStorage.contains('adobeMode');
    const useEmailApi = SessionStorage.contains('useEmailApi');
    const usePhoneApi = SessionStorage.contains('usePhoneApi');

    const [isButtonAwaitingResponse, setIsButtonAwaitingResponse] =
        useState<boolean>(false);

    const buildAppData = async (
        flow: AcquisitionFlow,
        programData: Record<string, any>,
        existingAppData?: Record<string, any>,
    ): Promise<Record<string, any>> => {
        const applicationData: any = existingAppData ? existingAppData : {};
        const acquisitionData = getPersonalInformation();
        const deviceInfo = blackboxUtils.getBlackbox();
        if (
            checkIfInValidAcquisitionFlow(
                [
                    AcquisitionFlow.PQ,
                    AcquisitionFlow.SkipPQ,
                    AcquisitionFlow.PqOfOne,
                ],
                flow,
            )
        ) {
            applicationData.AuthorizedUser = {
                IsAuthorizedUserSelected: getIsAuthorizedUserSelected(),
                IsAuthorizedUserSpouse: acquisitionData?.authorizedUserSpouse?.value,
                AuthorizedUserFirstName: acquisitionData?.authorizedUserFirstName?.value,
                AuthorizedUserMiddleInitial: acquisitionData?.authorizedUserMiddleInitial?.value,
                AuthorizedUserLastName: acquisitionData?.authorizedUserLastName?.value,
                AuthorizedUserDOBDay: acquisitionData?.authorizedUserDateOfBirthDay?.value,
                AuthorizedUserDOBMonth: acquisitionData?.authorizedUserDateOfBirthMonth?.value,
                AuthorizedUserDOBYear: acquisitionData?.authorizedUserDateOfBirthYear?.value,
            };
            applicationData.CreditProtectionSelected =
                creditProtectionInfo.CreditProtectionSelected;
            applicationData.CreditProtectionInitials =
                creditProtectionInfo.CreditProtectionInitials;
            applicationData.AgreedToPrivacyPolicy = isTermsAgreed;
            applicationData.IsBalanceTransferSelected = balanceTransferSelected;
            if (
                !props.splitApplication ||
                (props.splitApplication && props.firstPage)
            ) {
                applicationData.SelectedCardId = selectedCardId;
                if (
                    checkIfInValidAcquisitionFlow(
                        [
                            AcquisitionFlow.PQ,
                            AcquisitionFlow.SkipPQCounter,
                            AcquisitionFlow.PqOfOne,
                        ],
                        flow,
                    )
                ) {
                    const personalInfoData = await SessionStorage.tryGetCipher(
                        'personalInfo',
                        {},
                    );
                    applicationData.PersonalInformation = personalInfoData;
                    if (
                        checkIfInValidAcquisitionFlow(
                            [AcquisitionFlow.PQ, AcquisitionFlow.PqOfOne],
                            flow,
                        )
                    ) {
                        applicationData.PersonalInformation.PrimaryPhoneNumber =
                            acquisitionData?.phoneNumber?.value;
                        applicationData.PersonalInformation.MonthlyIncome =
                            acquisitionData?.monthlyIncome?.value;
                    }
                } else {
                    const dobArr = personalInfoForm.dob.value.split('/');
                    const isMailingHomeAddressSame =
                        !personalInfoForm.homeAddressShown;
                    applicationData.PersonalInformation = {
                        FirstName: acquisitionData?.firstName?.value,
                        MiddleInitial: acquisitionData.middleInitial.value,
                        LastName: acquisitionData?.lastName?.value,
                        EmailAddress: acquisitionData?.emailAddress.value,
                        MailingAddressLine1:
                            personalInfoForm.mailingAddress.value,
                        MailingAddressLine2:
                            personalInfoForm.mailingAptUnit.value ?? '',
                        MailingCity: personalInfoForm.mailingCity.value.trim(),
                        MailingState: personalInfoForm.mailingState.value,
                        MailingZipCode: personalInfoForm.mailingZip.value,
                        IsMailingHomeAddressSame: isMailingHomeAddressSame,
                        HomeAddressLine1: personalInfoForm.homeAddress.value,
                        HomeAddressLine2:
                            personalInfoForm.homeAptUnit.value ?? '',
                        HomeCity: personalInfoForm.homeCity.value.trim(),
                        HomeState: personalInfoForm.homeState.value,
                        HomeZipCode: personalInfoForm.homeZip.value,
                        PrimaryPhoneNumber: acquisitionData.phoneNumber.value
                            ? acquisitionData.phoneNumber.value.replace(
                                /[^\d]/g,
                                '',
                            )
                            : '',
                        MonthlyIncome: personalInfoForm.monthlyIncome.value,
                        Ssn: personalInfoForm.ssn.value,
                        DOBMonth: dobArr[0],
                        DOBDay: dobArr[1],
                        DOBYear: dobArr[2],
                    };

                    /*
                        DPM-13680: Logic to reswap values in state from their semantic type to their field type if both exist
                    */
                    const hasMailing = personalInfoForm.mailingAddress.value && personalInfoForm.mailingCity.value && personalInfoForm.mailingState.value && personalInfoForm.mailingZip.value;
                    const hasHome = personalInfoForm.homeAddress.value && personalInfoForm.homeCity.value && personalInfoForm.homeState.value && personalInfoForm.homeZip.value;
                    const containsMailingAndHomeAddresses = hasMailing && hasHome;
                    if (homeAddressPrimaryRef.current.value && containsMailingAndHomeAddresses) {                        
                        applicationData.PersonalInformation = {
                            ...applicationData.PersonalInformation,
                            MailingAddressLine1:
                                personalInfoForm.homeAddress.value,
                            MailingAddressLine2:
                                personalInfoForm.homeAptUnit.value ?? '',
                            MailingCity: personalInfoForm.homeCity.value.trim(),
                            MailingState: personalInfoForm.homeState.value,
                            MailingZipCode: personalInfoForm.homeZip.value,
                            IsMailingHomeAddressSame: isMailingHomeAddressSame,
                            HomeAddressLine1: personalInfoForm.mailingAddress.value,
                            HomeAddressLine2:
                                personalInfoForm.mailingAptUnit.value ?? '',
                            HomeCity: personalInfoForm.mailingCity.value.trim(),
                            HomeState: personalInfoForm.mailingState.value,
                            HomeZipCode: personalInfoForm.mailingZip.value,
                        }
                    }
                }
                // TODO: Mobile number fields are unused, need to check if api will throw error if we remove these
                applicationData.MobilePhoneNumber = {};
                applicationData.MobilePhoneNumber.Number = '';
                applicationData.MobilePhoneNumber.Agree = '';
            }

            applicationData.ConsumerAcknowledgment = isConsumerReportAgreed;
            applicationData.IsSecuredCard = !!programData.IsSecuredCard;
            applicationData.ApplicantESignInterest = Number(
                isEsignAgreed && isEsignShowing,
            );
            applicationData.ESignDisclosureVersion =
                isEsignShowing && esignVersion ? esignVersion : '';

            if (applicationData.IsSecuredCard) {
                applicationData.AgreedToFullESign = isEsignAgreed;
                if (!isFundingToggled()) {
                    applicationData.FundingPaymentSource =
                        fundingInfo.fundType === 'debit' ? 1 : 0;
                    if (fundingInfo.fundType === 'bank') {
                        applicationData.FundingInformation = {
                            AccountType: fundingInfo.acctType,
                            AccountNumber: fundingInfo.acctNumber,
                            RoutingNumber: fundingInfo.routingNumber,
                            AmountToFund: parseFloat(fundingInfo.fundAmount),
                            C1BAuthToFund: fundingInfo.isAgree,
                        };
                    } else if (fundingInfo.fundType === 'debit') {
                        applicationData.DebitFundingInformation = {
                            AmountToFund: parseFloat(fundingInfo.fundAmount),
                        };
                    }
                }
            }

            // TODO: According to documentation, this is a required field. Check to see if we actually need it
            applicationData.PersonalInformation.IsDataEntryPage = false;

            if (flow !== AcquisitionFlow.SkipPQCounter) {

                if (deviceInfo?.Blackbox) {
                    applicationData.PersonalInformation.ioBlackBox =
                        deviceInfo.Blackbox.ThirdParty;
                    applicationData.PersonalInformation.fpBB =
                        deviceInfo.Blackbox.FirstParty;
                } else {
                    applicationData.PersonalInformation.ioBlackBox = '';
                    applicationData.PersonalInformation.fpBB = '';
                }
            }
        } else if (flow === AcquisitionFlow.PA) {
            const isMailingHomeAddressSame = !personalInfoForm.homeAddressShown;
            applicationData.SolicitationId = parseInt(
                programData.SolicitationId,
            );
            applicationData.ReservationNumber = programData.ReservationNumber;
            if (
                !props.splitApplication ||
                (props.splitApplication && props.firstPage)
            ) {
                applicationData.Applicant = {
                    ApplicantName: {
                        FirstName: acquisitionData?.firstName?.value,
                        LastName: acquisitionData?.lastName?.value,
                        MiddleInitial: acquisitionData.middleInitial.value,
                        MiddleName: '',
                        NameSuffix: personalInfoForm.suffix.value,
                    },
                    SocialSecurityNumber: {
                        FullSocialSecurityNumber: personalInfoForm.ssn.value,
                    },
                    MailingAddress: {
                        AddressLineOne: personalInfoForm.mailingAddress.value,
                        AddressLineTwo: personalInfoForm.mailingAptUnit.value,
                        City: personalInfoForm.mailingCity.value.trim(),
                        State: personalInfoForm.mailingState.value,
                        ZipCode: personalInfoForm.mailingZip.value,
                        ZipPlusFour:
                            programData.Applicant.MailingAddress.ZipPlusFour,
                        AddressType:
                            programData.Applicant.MailingAddress.AddressType,
                    },
                    HomeAddress: {
                        AddressLineOne: isMailingHomeAddressSame
                            ? ''
                            : personalInfoForm.homeAddress.value,
                        AddressLineTwo: isMailingHomeAddressSame
                            ? ''
                            : personalInfoForm.homeAptUnit.value,
                        City: isMailingHomeAddressSame
                            ? ''
                            : personalInfoForm.homeCity.value.trim(),
                        State: isMailingHomeAddressSame
                            ? ''
                            : personalInfoForm.homeState.value,
                        ZipCode: isMailingHomeAddressSame
                            ? ''
                            : personalInfoForm.homeZip.value,
                        ZipPlusFour:
                            programData.Applicant.HomeAddress.ZipPlusFour,
                        AddressType:
                            programData.Applicant.HomeAddress.AddressType,
                    },
                    PrimaryPhoneNumber: {
                        Number: acquisitionData.phoneNumber.value
                            ? acquisitionData.phoneNumber.value.replace(
                                /[^\d]/g,
                                '',
                            )
                            : '',
                    },
                    SecondaryPhoneNumber: {
                        Number: '',
                    },
                    EmailAddress: {
                        Value: acquisitionData?.emailAddress.value,
                    },
                    DateOfBirth: personalInfoForm.dob.value,
                    MonthlyIncome: personalInfoForm.monthlyIncome.value,
                    CreditScore: programData.Applicant.CreditScore,
                    IsMonthlyIncomeNull:
                        programData.Applicant.IsMonthlyIncomeNull,
                    MonthlyExpenses: programData.Applicant.MonthlyExpenses,
                    IsAddressChangedForSolicitation:
                        programData.Applicant.IsAddressChangedForSolicitation,
                    IsMailingAddressSameAsHomeAddress: isMailingHomeAddressSame,
                };
                applicationData.SelectedProgramPlasticCode = selectedCardId;
            }
            applicationData.AuthorizedUser = {
                IsAuthorizedUserSelected: getIsAuthorizedUserSelected(),
                IsAuthorizedUserSpouse: acquisitionData?.authorizedUserSpouse?.value,
                AuthorizedUserFirstName: acquisitionData?.authorizedUserFirstName?.value,
                AuthorizedUserMiddleInitial: acquisitionData?.authorizedUserMiddleInitial?.value,
                AuthorizedUserLastName: acquisitionData?.authorizedUserLastName?.value,
                AuthorizedUserDOBDay: acquisitionData?.authorizedUserDateOfBirthDay?.value,
                AuthorizedUserDOBMonth: acquisitionData?.authorizedUserDateOfBirthMonth?.value,
                AuthorizedUserDOBYear: acquisitionData?.authorizedUserDateOfBirthYear?.value,
            };
            applicationData.CreditProtection = {
                IsCreditProtectionSelected:
                    creditProtectionInfo.CreditProtectionSelected,
                CreditProtectionInitials:
                    creditProtectionInfo.CreditProtectionInitials,
            };
            applicationData.DeviceInformation = deviceInfo;
            applicationData.BlackBox = deviceInfo?.Blackbox;
            applicationData.MarketingAttributes =
                getMarketingAttributesForAcquisitions();
            applicationData.IsBalanceTransferSelected = balanceTransferSelected;
            applicationData.ApplicantESignInterest = Number(
                isEsignAgreed && isEsignShowing,
            );
            applicationData.ESignDisclosureVersion =
                isEsignShowing && esignVersion ? esignVersion : '';
            applicationData.ConsumerAcknowledgment = isConsumerReportAgreed;
        }
        return applicationData;
    };

    const buildFlowApplicationData = (
        programData: Record<string, any>,
    ): Record<string, any> => {
        const flowApplicationData: any = { ...programData };
        const deviceInfo = blackboxUtils.getBlackbox();
        const acquisitionData = getPersonalInformation();

        flowApplicationData.programCode = programData.offers[0].card.programCode;

        flowApplicationData.AuthorizedUser = {
            IsAuthorizedUserSelected: getIsAuthorizedUserSelected(),
            IsAuthorizedUserSpouse: acquisitionData?.authorizedUserSpouse?.value,
            AuthorizedUserFirstName: acquisitionData?.authorizedUserFirstName?.value,
            AuthorizedUserMiddleInitial: acquisitionData?.authorizedUserMiddleInitial?.value,
            AuthorizedUserLastName: acquisitionData?.authorizedUserLastName?.value,
            AuthorizedUserDOBDay: acquisitionData?.authorizedUserDateOfBirthDay?.value,
            AuthorizedUserDOBMonth: acquisitionData?.authorizedUserDateOfBirthMonth?.value,
            AuthorizedUserDOBYear: acquisitionData?.authorizedUserDateOfBirthYear?.value,
        };
        flowApplicationData.creditProtectionSelected =
            creditProtectionInfo.CreditProtectionSelected;
        flowApplicationData.creditProtectionInitials =
            creditProtectionInfo.CreditProtectionInitials;
        flowApplicationData.SelectedCardId = selectedCardId;
        flowApplicationData.AgreedToPrivacyPolicy = isTermsAgreed;
        flowApplicationData.IsBalanceTransferSelected = balanceTransferSelected;
        flowApplicationData.SelectedProgramPlasticCode = selectedCardId;
        flowApplicationData.DeviceInformation = deviceInfo;
        flowApplicationData.BlackBox = deviceInfo.Blackbox;
        flowApplicationData.MarketingAttributes =
            getMarketingAttributesForAcquisitions();
        flowApplicationData.ApplicantESignInterest = Number(isEsignAgreed);
        flowApplicationData.AgreedToFullESign = isEsignAgreed;
        flowApplicationData.ESignDisclosureVersion = esignVersion;
        flowApplicationData.ConsumerAcknowledgment = isConsumerReportAgreed;
        flowApplicationData.IsMailingHomeAddressSame =
            programData.parties[0].addresses.length === 1;
        flowApplicationData.IsSecuredCard = programData.offerSecuredCard;

        flowApplicationData.parties[0].identification = {
            type: 'SSN',
            value: ssnInfo,
        };

        return flowApplicationData;
    };

    const handleSuccess = async (flow: AcquisitionFlow) => {
        const programData = await getProgramData();
        let redirectUrl = props.defaultRedirectUrl;

        if (programData?.IsSecuredCard || programData?.offerSecuredCard) {
            if (isFundingToggled()) {
                redirectUrl = props.fundingToggleRedirect;
            }
        }

        let appData;
        let existingAppData;
        try {
            if (props.splitApplication && !props.firstPage) {
                existingAppData = checkIfInValidAcquisitionFlow(
                    [
                        AcquisitionFlow.PQ,
                        AcquisitionFlow.SkipPQ,
                        AcquisitionFlow.PqOfOne,
                    ],
                    flow,
                )
                    ? await SessionStorage.tryGetCipher(
                        'prequalFullAppData',
                        {},
                    )
                    : flow === AcquisitionFlow.PHA
                        ? SessionStorage.tryGetObject('flowApplicationData', {})
                        : await SessionStorage.tryGetCipher(
                            'preApprovalFullAppData',
                            {},
                        );
            }
            appData =
                flow === AcquisitionFlow.PHA
                    ? buildFlowApplicationData(programData)
                    : await buildAppData(flow, programData, existingAppData);
        } catch (e) {
            throw new Error('Unable to build appData');
        }

        if (
            checkIfInValidAcquisitionFlow(
                [
                    AcquisitionFlow.PQ,
                    AcquisitionFlow.SkipPQ,
                    AcquisitionFlow.PqOfOne,
                ],
                flow,
            )
        ) {
            await SessionStorage.setCipherWithCurrentScope(
                'prequalFullAppData',
                appData,
            );
        } else if (flow === AcquisitionFlow.PHA) {
            SessionStorage.setObjectWithCurrentScope(
                'flowApplicationData',
                appData,
            );
        } else {
            await SessionStorage.setCipherWithCurrentScope(
                'preApprovalFullAppData',
                appData,
            );

            if (
                SessionStorage.verify('pre-approval-data') &&
                appData.Applicant
            ) {
                const preApprovalData = await SessionStorage.getCipher(
                    'pre-approval-data',
                    true,
                );
                preApprovalData.Applicant = appData.Applicant;
                await SessionStorage.setCipher(
                    'pre-approval-data',
                    preApprovalData,
                );
            }
        }

        const cardInfo = SessionStorage.tryGetObject('cardInfo', []);
        const marketingAttributes = getMarketingAttributesForAcquisitions();

        await flowDataModule.setInSession({
            MarketingAttributes: marketingAttributes,
        });

        if (flow !== AcquisitionFlow.PHA) {
            await flowDataModule.updateInSession({
                FirstName: appData.PersonalInformation
                    ? appData.PersonalInformation.FirstName
                    : appData.Applicant.ApplicantName.FirstName,
                MonthlyIncome: appData.PersonalInformation
                    ? appData.PersonalInformation.MonthlyIncome
                    : appData.Applicant.MonthlyIncome,
                ZipCode: appData.PersonalInformation
                    ? appData.PersonalInformation.MailingZipCode
                    : appData.Applicant.MailingAddress.ZipCode,
                IsAuthorizedUserSelected:
                    appData.AuthorizedUser.IsAuthorizedUserSelected,
                IsCreditProtectionSelected: appData.CreditProtection
                    ? appData.CreditProtection.CreditProtectionSelected
                    : appData.CreditProtectionSelected,
                IsBalanceTransferSelected: appData.IsBalanceTransferSelected,
                SelectedProgramPlasticCode: cardInfo[selectedCardId - 1],
            });
        } else {
            await flowDataModule.updateInSession({
                FirstName: appData?.parties?.[0]?.firstName,
                MonthlyIncome: appData?.parties?.[0]?.income?.amount,
                ZipCode: appData?.parties?.[0]?.addresses?.find(
                    addr => addr.usageType === 'MAILING',
                )?.zipCode,
                IsAuthorizedUserSelected:
                    appData.AuthorizedUser.IsAuthorizedUserSelected,
                IsCreditProtectionSelected: appData.creditProtectionSelected,
                IsBalanceTransferSelected: appData.IsBalanceTransferSelected,
                SelectedProgramPlasticCode: cardInfo[selectedCardId - 1],
            });
        }

        window.location.assign(`${redirectUrl}${window.location.search}`);
    };

    const isSamePersonCheck = async (isSecondPage: boolean) => {
        if (isAUSamePersonCheckDisabled) {
            return false;
        }
        const acquisitionData = getPersonalInformation();
        let authorizedUserDob = `${authorizedUserInfo.AuthorizedUserDOBMonth}/${authorizedUserInfo.AuthorizedUserDOBDay}/${authorizedUserInfo.AuthorizedUserDOBYear}`;            

        let appDataFirstName = '';
        let appDataLastName = '';
        let appDataDOB = '';

        [appDataFirstName, appDataLastName, appDataDOB] =
            await getNameAndDOBFromSessionStorage(isSecondPage);

        const appDataIsNotEmpty =
            appDataFirstName && appDataLastName && appDataDOB;

        const authorizedUserNotEmpty = !!acquisitionData?.authorizedUserFirstName?.value && !!acquisitionData?.authorizedUserLastName?.value;
        const personalInfoFormNotEmpty =
            !!acquisitionData?.firstName?.value &&
            !!acquisitionData?.lastName?.value &&
            !!personalInfoForm?.dob?.value;
        const appDataApplicant = [appDataFirstName,appDataLastName,appDataDOB].join('');
        const formAuthorizedUser = [
            acquisitionData?.authorizedUserFirstName?.value,
            acquisitionData?.authorizedUserLastName?.value,
            authorizedUserDob
        ].join('');
        const isSamePersonFromSessionStorage =
            appDataIsNotEmpty &&
            authorizedUserNotEmpty &&
            areStringsEqual(appDataApplicant, formAuthorizedUser);

        const formApplicant = [
            acquisitionData?.firstName?.value,
            acquisitionData?.lastName?.value,
            personalInfoForm.dob.value
        ].join('');
        const isSamePersonFromForm =
            personalInfoFormNotEmpty &&
            authorizedUserNotEmpty &&
            areStringsEqual(formApplicant, formAuthorizedUser);

        let isSamePersonForCounterApp;
        if (SessionStorage.verifyNotEmpty('personalInfo')) {
            const personalInfo = await SessionStorage.getCipher('personalInfo', true);
            const dob =
                personalInfo?.DOBMonth +
                '/' +
                personalInfo?.DOBDay +
                '/' +
                personalInfo?.DOBYear;
            const applicantFromSessionStorage = [
                personalInfo?.FirstName,
                personalInfo?.LastName,
                dob
            ].join('');
            isSamePersonForCounterApp =
                authorizedUserNotEmpty &&
                areStringsEqual(applicantFromSessionStorage, formAuthorizedUser);
        }

        const isSamePerson =
            (authorizedUserNotEmpty && isSamePersonFromSessionStorage) ||
            (personalInfoFormNotEmpty && isSamePersonFromForm) ||
            (authorizedUserNotEmpty && isSamePersonForCounterApp);
        return isSamePerson;
    };

    const { handleErrorPushdown } = useHandleErrorPushdown(isSamePersonCheck);

    const validateForm = async (): Promise<void> => {
        closePushdown();
        const validatedMap = {
            ['application-esign']: !isEsignShowing || isEsignLinkValid,
            ['authorized-user']: isAuthorisedUserSectionValid,
            ['consumer-report']: isConsumerReportAgreed,
            ['credit-protection']: checkIsCreditProtectionValid(),
            ['funding-module']: isFundingSectionValid,
            ['personal-information-form']: validatePersonalInformation(),
            ['phone-number-and-income']: getIsPhoneNumberValid() && acquisitionData?.phoneNumber?.value && isMonthlyIncomeValid && acquisitionData?.monthlyIncome?.value,
            ['plastic-card-design']: !!selectedCardId,
            ['ssn-module']: isSsnInfoValid && ssnInfo?.length === 9,
            ['terms-and-conditions']: isTermsValid && isTermsAgreed,
        };

        for (const mappingName in validatedMap) {
            if (validatedMap[mappingName]) continue;

            const hasComponent = hasDefaultComponent(
                'cob-corp-acquisition/components/creditonebank/' +
                mappingName,
            );

            if (hasComponent) {
                return Promise.reject();
            }
        }

        if (validationRef.current && !validationRef.current.isValid && validationRef.current.data) {
            return Promise.reject();
        } else if (validationRef.current && validationRef.current.isValid && validationRef.current.data.commercial) {
            const isHomeAddressInvalid = personalInfoForm.homeAddress.value &&
                !allow.home_address.test(personalInfoForm.homeAddress.value);
            const isHomeCityInvalid = personalInfoForm.homeCity.value && !city.test(personalInfoForm.homeCity.value);
            const isHomeZipInvalid = personalInfoForm.homeZip.value && !zip_code.test(personalInfoForm.homeZip.value);

            if (isHomeAddressInvalid || isHomeCityInvalid || isHomeZipInvalid || !personalInfoForm.homeState.value) {
                return Promise.reject({ message: 'loqate home error' });
            }
        }

        return Promise.resolve();
    };

    // Refactor this function as part of https://creditonebank.atlassian.net/browse/DPM-12192
    const dispatchAcquisitionErrorEvent = () => {
        const toDispatch = personalInfoForm;
        if (acquisitionData?.monthlyIncome?.value) {
            personalInfoForm.monthlyIncome.value = acquisitionData.monthlyIncome.value;
        }
        if (acquisitionData?.phoneNumber?.value) {
            personalInfoForm.phoneNumber.value = acquisitionData.phoneNumber.value;
        }
        window.dispatchEvent(
            new AcquisitionErrorToggleEvent(toDispatch),
        );
    }

    const handleSubmit = async (e: FormEvent<HTMLButtonElement>) => {
        e.preventDefault();
        
        const acquisitionData = getPersonalInformation();
        await addressValidationSemaphore.acquire();
        const isSecondPage = props.splitApplication && !props.firstPage;
        const flow = getAcquisitionFlow();
        const isEmailInError = acquisitionData?.emailAddress?.value && ((!isAuthor && !isAdobeMode) || (isAdobeMode && useEmailApi)) && flow !== AcquisitionFlow.PqOfOne && !(await emailValidationApi(acquisitionData.emailAddress.value));
        const isPhoneInError = acquisitionData?.phoneNumber?.value && ((!isAuthor && !isAdobeMode) || (isAdobeMode && usePhoneApi)) && !(await phoneValidationApi(acquisitionData.phoneNumber.value.replace(/[^0-9]/g, '').slice(0, 10)));
        if(isEmailInError || isPhoneInError) {
            dispatchAcquisitionErrorEvent();
            await handleErrorPushdown(isSecondPage, undefined, homeAddressPrimaryRef.current.value);
            setShouldRenderLoader(false);
            addressValidationSemaphore.release();
            return;
        }
        
        if (await isSamePersonCheck(isSecondPage)) {
            setIsAuthorisedUserSectionValid(false);
            dispatchAcquisitionErrorEvent();
            await handleErrorPushdown(isSecondPage, undefined, homeAddressPrimaryRef.current.value);
            setShouldRenderLoader(false);
            addressValidationSemaphore.release();
            return;
        }

        validateForm()
            .then(async () => {
                setShouldRenderLoader(true);
                setIsButtonAwaitingResponse(true);
                if (
                    checkIfInValidAcquisitionFlow(
                        [
                            AcquisitionFlow.PQ,
                            AcquisitionFlow.PqOfOne,
                            AcquisitionFlow.PHA,
                            AcquisitionFlow.SkipPQCounter,
                        ],
                        flow,
                    ) 
                    || (props.splitApplication && !props.firstPage)
                ) {
                    await handleSuccess(flow);
                } else {
                    const stateZipBody = {
                        state: personalInfoForm.mailingState.value,
                        zipCode: personalInfoForm.mailingZip.value,
                    };

                    acquisitionApiRequest<boolean>(
                        'validate-state-and-zip',
                        { body: stateZipBody, method: 'POST' },
                        initResponse,
                    )
                        .then(async response => {
                            if (response.data) {
                                await handleSuccess(flow);
                            } else {
                                showPushdown('zipError');
                                setIsButtonAwaitingResponse(false);
                                setShouldRenderLoader(false);
                            }
                        })
                        .catch(error => {
                            if (error.response.status === 408) {
                                clearAquisitionData();
                                const params = new URLSearchParams(
                                    window.location.search,
                                );
                                params.set(
                                    'errorCode',
                                    props.serverErrorCode as string,
                                );
                                window.location.assign(
                                    props.dataEntryUrl +
                                    '?' +
                                    params.toString(),
                                );
                            } else if (error.response.status === 500) {
                                clearAquisitionData();
                                const params = new URLSearchParams(
                                    window.location.search,
                                );
                                params.set('errorCode', 'processingError');
                                window.location.assign(
                                    props.dataEntryUrl +
                                    '?' +
                                    params.toString(),
                                );
                            } else {
                                showPushdown(
                                    props.serverSessionExpiredErrorCode as string,
                                );
                                setIsButtonAwaitingResponse(false);
                            }
                            setShouldRenderLoader(false);
                        });
                }
            })
            .catch(async (error) => {
                dispatchAcquisitionErrorEvent();
                await handleErrorPushdown(isSecondPage, error?.message, homeAddressPrimaryRef.current.value);
                setShouldRenderLoader(false);
            });

        addressValidationSemaphore.release();
    };

    const fetchDisclaimer = () => {
        if (
            !props.contentFragmentFolder ||
            !SessionStorage.verify('selectedProgramCode')
        ) {
            return;
        }
        const servlet = '/services/v2/contentFragmentServlet';
        const path = props.contentFragmentFolder;
        const programCode = SessionStorage.get('selectedProgramCode');
        const filters = JSON.stringify({ programCodes: programCode });

        axios
            .get<any, { data: IDisclaimerContent[] }>(
                servlet +
                '.path=' +
                path +
                '.filters=' +
                filters +
                '.optional=true',
            )
            .then((result: { data: IDisclaimerContent[] }) => {
                if (result.data[0].disclaimerText) {
                    setDynamicDisclaimer(result.data[0].disclaimerText);
                } else {
                    throw { response: result };
                }
            })
            .catch(() => { });
    };

    useEffect(() => {
        setShowInlinePushups(true);

        fetchDisclaimer();
        const disclosureText = PathUtils.getMetaPropertyValue(
            'SkipPqDisclosureContent',
        );
        if (disclosureText) {
            setDynamicDisclaimer(disclosureText);
        }
    }, []);

    useEffect(() => {
        if (isApplicationInfoRecieved) {
            fetchDisclaimer();
        }
    }, [isApplicationInfoRecieved]);

    const handleStorageChange = (event: StorageEvent) => {
        console.error({
            StorageEvent: event.key,
            OldValue: event.oldValue,
            NewValue: event.newValue,
        });
    };

    useEffect(() => {
        const skipPa = SessionStorage.tryGet('skipPa', false);
        if (skipPa && isIOS()) {
            window.addEventListener('storage', handleStorageChange);
        }
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const [messages, setMessages] = useState<string[]>([]);

    const acquisitionData = getPersonalInformation();
    const emailErrorType = getFeatureFlags('DPM-13566')
        ? acquisitionData.emailAddress.errorType
        : personalInfoErrors.EmailAddress;
    let personalInformationErrorMap = {
        firstName:
            generateErrorMessage('<a href="#firstName-field-container" id="cta-firstName">First Name</a>', !acquisitionData.firstName.value ? 'missingValue' : null),
        lastName:
            generateErrorMessage('<a href="#lastName-field-container" id="cta-lastName">Last Name</a>', !acquisitionData.lastName.value ? 'missingValue' : null),
        suffix: generateErrorMessage('<a href="#suffix-field-container">Suffix</a>'),
        mailingAddressLineOne:
            generateErrorMessage('<a href="#mailingAddress-field-container" id="cta-mailingAddress">Mailing Address</a>', !acquisitionData.mailingAddressLineOne.value ? 'missingValue': 'thirdParty', 'address'),
        mailingAddressCity:
            generateErrorMessage(`<a href="#mailingCity-field-container" id="cta-mailingCity">${!isNewErrorMessagingFlagActive ? 'Mailing Address ' : ''}City</a>`, !acquisitionData.mailingAddressCity.value ? 'missingValue' : null),
        mailingAddressState:
            generateErrorMessage(`<a href="#mailingState-field-container" id="cta-mailingState">${!isNewErrorMessagingFlagActive ? 'Mailing Address ' : ''}State</a>`, !acquisitionData.mailingAddressState.value ? 'missingValue' : null),
        mailingAddressZipCode:
            generateErrorMessage(`<a href="#mailingZipCode-field-container" id="cta-mailingZipCode">${!isNewErrorMessagingFlagActive ? 'Mailing Address ' : ''}ZIP Code</a>`, !acquisitionData.mailingAddressZipCode.value ? 'missingValue' : null),
        homeAddressLineOne:
            generateErrorMessage('<a href="#homeAddress-field-container" id="cta-homeAddress">Home Street Address</a>', !acquisitionData.homeAddressLineOne.value ? 'missingValue' : 'thirdParty', 'address'),
        homeAddressCity:
            generateErrorMessage('<a href="#homeCity-field-container" id="cta-homeCity">Home Address City</a>', !acquisitionData.homeAddressCity.value ? 'missingValue' : null),
        homeAddressState:
            generateErrorMessage('<a href="#homeState-field-container" id="cta-homeState">Home Address State</a>', !acquisitionData.homeAddressState.value ? 'missingValue' : null),
        homeAddressZipCode:
            generateErrorMessage('<a href="#homeZipCode-field-container" id="cta-homeZipCode">Home Address ZIP Code</a>', !acquisitionData.homeAddressZipCode.value ? 'missingValue' : null),
        emailAddress: !isNewErrorMessagingFlagActive
            ? 'Please provide your <a href="#emailAddress-field-container" id="cta-emailAddress">Email Address (email@domain.com)</a>'
            : emailErrorType === 'RequiredField' || emailErrorType === 'requiredField'
            ? generateErrorMessage('<a href="#emailAddress-field-container" id="cta-emailAddress">Email Address</a>', 'missingValue') + ': email@domain.com'
            : emailErrorType === 'NotValid' || emailErrorType === 'invalidInput'
                ? generateEmailFormatErrorMessage('<a href="#emailAddress-field-container" id="cta-emailAddress">Email Address</a>')
                : generateErrorMessage('<a href="#emailAddress-field-container" id="cta-emailAddress">Email Address</a>', 'thirdParty', 'Email'),
        phoneNumber:
            generateErrorMessage(`<a href="#phone-field-container" id="cta-phone">${isNewErrorMessagingFlagActive ? '' : 'Main '}Phone Number</a>`, acquisitionData.phoneNumber.value?.replace(/[^0-9]/g, '').length === 10 ? 'thirdParty' : 'missingValue', 'Number'),
        monthlyIncome:
            generateMonthlyIncomeErrorMessage('<a href="#monthlyIncome-field-container" id="cta-monthlyIncome">Total Monthly Income</a>', acquisitionData.monthlyIncome.value || ''),
        ssn: generateErrorMessage('<a href="#socialSecurity-field-container" id="cta-socialSecurity">Social Security Number</a>', !acquisitionData.ssn.value ? 'missingValue' : null),
        dob: !isNewErrorMessagingFlagActive
        ? 'Please provide your <a href="#dob-field-container" id="cta-dob">Date of Birth (MM-DD-YYYY)</a>'
        : generateErrorMessage('<a href="#dob-field-container" id="cta-dob">Date of Birth</a>', personalInfoForm.dob.value === '' ? 'missingValue' : null),
    };

    if (homeAddressPrimaryRef.current.value) {
        personalInformationErrorMap = {
            ...personalInformationErrorMap,
            mailingAddressLineOne:
                generateErrorMessage('<a href="#mailingAddress-field-container" id="cta-mailingAddress">Home Address</a>', !acquisitionData.mailingAddressLineOne.value ? 'missingValue': 'thirdParty', 'address'),
            mailingAddressCity:
                generateErrorMessage('<a href="#mailingCity-field-container" id="cta-mailingCity">Home Address City</a>', !acquisitionData.mailingAddressCity.value ? 'missingValue' : null),
            mailingAddressState:
                generateErrorMessage('<a href="#mailingState-field-container" id="cta-mailingState">Home Address State</a>', !acquisitionData.mailingAddressState.value ? 'missingValue' : null),
            mailingAddressZipCode:
                generateErrorMessage('<a href="#mailingZipCode-field-container" id="cta-mailingZipCode">Home Address ZIP Code</a>', !acquisitionData.mailingAddressZipCode.value ? 'missingValue' : null),
            homeAddressLineOne:
                generateErrorMessage('<a href="#homeAddress-field-container" id="cta-homeAddress">Mailing Street Address</a>', !acquisitionData.homeAddressLineOne.value ? 'missingValue' : 'thirdParty', 'address'),
            homeAddressCity:
                generateErrorMessage('<a href="#homeCity-field-container" id="cta-homeCity">Mailing Address City</a>', !acquisitionData.homeAddressCity.value ? 'missingValue' : null),
            homeAddressState:
                generateErrorMessage('<a href="#homeState-field-container" id="cta-homeState">Mailing Address State</a>', !acquisitionData.homeAddressState.value ? 'missingValue' : null),
            homeAddressZipCode:
                generateErrorMessage('<a href="#homeZipCode-field-container" id="cta-homeZipCode">Mailing Address ZIP Code</a>', !acquisitionData.homeAddressZipCode.value ? 'missingValue' : null),
        };
    }

    const miscErrorMap = {
        cardDesign:
            isNewErrorMessagingFlagActive ? '<a href="#plastic-card-design" id="cta-plastic-card-design">Card Design</a> selection is required' : 'Please select a <a href="#plastic-card-design" id="pushdown-plastic-card-design">Card Design</a>',
        creditProtectionInitials:
            isNewErrorMessagingFlagActive ? 'Please provide your <a href="#creditProtection-field-container" id="cta-creditProtection">initials to confirm enrollment</a>' : 'Please provide your <a href="#creditProtection-field-container" id="cta-creditProtection">Initials to confirm enrollment of Credit Protection</a>',
        authorizedUserFirstName:
            '<a href="#auFirstName-field-container" id="cta-auFirstName">Authorized User\'s First Name</a>',
        authorizedUserLastName:
            '<a href="#auLastName-field-container" id="cta-auLastName">Authorized User\'s Last Name</a>',
        authorizedUserDob:
            isNewErrorMessagingFlagActive ? generateErrorMessage('<a href="#auDateOfBirth-field-container" id="cta-auDateOfBirth">Authorized User\'s Date of Birth</a>', !acquisitionData?.authorizedUserDateOfBirthDay?.value ? 'missingValue' : null) : 'Please provide the <a href="#auDateOfBirth-field-container" id="cta-auDateOfBirth">Authorized User\'s Date of Birth (MM-DD-YYYY)</a>',
        authorizedUserSpouse:
            isNewErrorMessagingFlagActive ? 'Select if <a href="#auSpouse-field-container" id="cta-auSpouse">Authorized User is your spouse</a>' : 'Please select if <a href="#auSpouse-field-container" id="cta-auSpouse">Authorized User is your spouse</a>',
        AddrAndAptNumMatch: 'Mailing address cannot be the same as apt/unit#',
        HomeAddrAndAptNumMatch: 'Home address cannot be the same as apt/unit#',
    };

    useEffect(() => {
        if (
            !personalInfoForm || 
            !authorizedUserValidity ||
            (
                getModernFields().length > 0 && !getPersonalInformation()
            )
        ) {
            return;
        }

        const setErrorMessages = async () => {
            // object to avoid duplicate entries
            const errorMessages = {};
            const acquisitionData = getPersonalInformation();
            const isDobValid = acquisitionData?.dateOfBirthDay?.state !== 'error' &&
                acquisitionData?.dateOfBirthMonth?.state !== 'error' &&
                acquisitionData?.dateOfBirthYear?.state !== 'error';
            const hasSsnModule = hasDefaultComponent('cob-corp-acquisition/components/creditonebank/ssn-module');

            Object.entries(personalInformationErrorMap).map(
                ([key, message]) => {
                    if ((key === 'ssn' && hasSsnModule) || key === 'monthlyIncome' ) {
                        return;
                    }

                    if (key === 'dob' && !isDobValid) {
                        errorMessages[key] = message;
                    } else if (Object.prototype.hasOwnProperty.call(acquisitionData, key) && acquisitionData[key].state === 'error') {
                        errorMessages[key] = message;
                    }
                },
            );

            if (hasSsnModule && !isSsnInfoValid) {
                errorMessages['ssn'] = personalInformationErrorMap.ssn;
            }
            if (acquisitionData.phoneNumber.errorType !== 'None' && acquisitionData.phoneNumber.state === 'error') {
                errorMessages['phoneNumber'] = personalInformationErrorMap.phoneNumber;
            }
            if (!isMonthlyIncomeValid) {
                if (isMonthlyOrYearly) {
                    let toFind;
                    let toReplace;

                    if (isMonthlyOrYearly === 'monthly') {
                        toFind = 'Yearly';
                        toReplace = 'Monthly';
                    } else {
                        toFind = 'Monthly';
                        toReplace = 'Yearly';
                    }

                    errorMessages['monthlyIncome'] = personalInformationErrorMap.monthlyIncome.replaceAll(toFind, toReplace);
                } else {
                    errorMessages['monthlyIncome'] = personalInformationErrorMap.monthlyIncome;
                }
            }

            const isSamePerson = (await isSamePersonCheck(props.splitApplication && !props.firstPage));
            if (isSamePerson) {
                errorMessages['authorizedUserFirstName'] = miscErrorMap.authorizedUserFirstName;
                errorMessages['authorizedUserLastName'] = miscErrorMap.authorizedUserLastName;
                errorMessages['authorizedUserDob'] = miscErrorMap.authorizedUserDob;
            }

            for await (const [key, message] of Object.entries(miscErrorMap)) {
                switch (key) {
                    case 'authorizedUserFirstName':
                    case 'authorizedUserLastName':
                        if (
                            acquisitionData?.[key]?.state === 'error'
                        ) {
                            errorMessages[key] = generateErrorMessage(message, !acquisitionData?.[key]?.value ? 'missingValue' : null);
                        }
                        break;
                    case 'authorizedUserSpouse':
                        if (
                            acquisitionData?.[key]?.state === 'error'
                        ) {
                            errorMessages[key] = message;
                        }
                        break;
                    case 'authorizedUserDob':
                        if (
                            acquisitionData?.['authorizedUserDateOfBirthDay']?.state === 'error' ||
                            acquisitionData?.['authorizedUserDateOfBirthMonth']?.state === 'error' ||
                            acquisitionData?.['authorizedUserDateOfBirthYear']?.state === 'error'
                        ) {
                            errorMessages[key] = message;
                        }
                        break;
                    case 'cardDesign':
                        if (!getIsSelectedCardValid()) {
                            errorMessages[key] = message;
                        }
                        break;
                    case 'creditProtectionInitials':
                        if (!getIsCreditProtectionValid()) {
                            errorMessages[key] = message;
                        }
                        break;
                    default: break;
                }
            }

            setMessages(sortPushupErrors(errorMessages));
        };

        setErrorMessages();
    }, [
        fields,
        personalInfoForm,
        personalInfoErrors,
        isCreditProtectionInfoValid,
        authorizedUserValidity,
        authorizedUserInfo,
        isTermsAgreed,
        isConsumerReportAgreed,
        getIsPhoneNumberValid(),
        isMonthlyIncomeValid,
        isSsnInfoValid,
        selectedCardValid,
        isMonthlyOrYearly,
    ]);

    return (
        <div id='application-cta' className='ApplicationCta'>
            {props.ctaDisclaimerText && (
                <div className='ApplicationCta__disclaimer'>
                    {transformHTMLParserLinks(props.ctaDisclaimerText)}
                </div>
            )}
            {showInlinePushups && <InlinePushup messages={messages} />}
            <Button
                className='ApplicationCta__cta'
                onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) =>
                    e.preventDefault()
                }
                onMouseUp={(e: any) => e.target.focus()}
                onClick={(e: FormEvent<HTMLButtonElement>) => {
                    if (
                        getAcquisitionFlow() === AcquisitionFlow.SkipPQ &&
                        !SessionStorage.verifyNotEmpty('initResponse')
                    ) {
                        acquisitionApiRequest<AcquisitionInitResponse>(
                            'init-compare-api',
                            {},
                        ).then(async response => {
                            SessionStorage.setObject(
                                'initResponse',
                                response.data,
                            );
                            handleSubmit(e);
                        });
                        return;
                    }
                    handleSubmit(e);
                }}
                id='full-app-continue'
                size='medium'
                target='_self'
                variant='solid'
                disabled={(
                    (!props.splitApplication && (!canFullAppContinue || !isTermsValid))
                    || (props.splitApplication && !props.firstPage && (!canFullAppContinue || !isTermsValid))
                    || isButtonAwaitingResponse
                    || status === 'unusable'
                )}
            >
                {
                    transformHTMLParserLinks(
                        wrapTrademarkSuperscript(props.ctaText),
                    ) as unknown as ReactElement
                }
            </Button>
            {dynamicDisclaimer && (
                <div className='ApplicationCta__dynamicDisclaimer'>
                    {transformHTMLParserLinks(dynamicDisclaimer)}
                </div>
            )}
        </div>
    );
};

export default ApplicationCta;
